import { useState, useEffect } from "react"
import { Bell, Check, MessageCircle, Heart, UserPlus, AtSign, Trash2, Loader2 } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { Button } from "@/components/ui/button"
import { UserAvatar } from "@/components/users/UserAvatar"
import { cn } from "@/lib/utils"
import { useAuth } from "@/context/AuthContext"
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/db"
import { supabase } from "@/lib/supabase"
import type { NotificationWithActor } from "@/types"

type NotificationType = "comment" | "reply" | "upvote" | "follow" | "mention"

const notificationIcons: Record<NotificationType, typeof Bell> = {
  comment: MessageCircle,
  reply: MessageCircle,
  upvote: Heart,
  follow: UserPlus,
  mention: AtSign,
}

const notificationMessages: Record<NotificationType, (n: NotificationWithActor) => string> = {
  comment: n => `commented on "${n.postTitle}"`,
  reply: n => `replied to your comment on "${n.postTitle}"`,
  upvote: n => `upvoted your post "${n.postTitle}"`,
  follow: () => "started following you",
  mention: n => `mentioned you in "${n.postTitle}"`,
}

export default function NotificationsPage() {
  const { firebaseUser } = useAuth()
  const [notifications, setNotifications] = useState<NotificationWithActor[]>([])
  const [loading, setLoading] = useState(true)
  const unreadCount = notifications.filter(n => !n.isRead).length

  useEffect(() => {
    if (!firebaseUser) return

    async function loadNotifications() {
      try {
        const data = await getNotifications(firebaseUser!.id)
        setNotifications(data)
      } catch (error) {
        console.error("Failed to load notifications:", error)
      } finally {
        setLoading(false)
      }
    }

    loadNotifications()

    // Subscribe to real-time notification updates
    const channel = supabase
      .channel("notifications-page")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${firebaseUser.id}` }, () => {
        loadNotifications()
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [firebaseUser])

  const markAllAsRead = async () => {
    if (!firebaseUser) return
    try {
      await markAllNotificationsRead(firebaseUser.id)
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    } catch (error) {
      console.error("Failed to mark all as read:", error)
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await markNotificationRead(id)
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)))
    } catch (error) {
      console.error("Failed to mark notification as read:", error)
    }
  }

  const clearAll = () => {
    setNotifications([])
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          {unreadCount > 0 && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{unreadCount} unread</p>}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <Check className="mr-2 h-4 w-4" />
              Mark all read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearAll}>
              <Trash2 className="mr-2 h-4 w-4" />
              Clear all
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : notifications.length > 0 ? (
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {notifications.map(notification => {
            const Icon = notificationIcons[notification.type]
            const actorName = notification.actor?.displayName || "Someone"
            const actorAvatar = notification.actor?.avatarUrl
            return (
              <div
                key={notification.id}
                className={cn(
                  "flex gap-4 p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer",
                  !notification.isRead && "bg-indigo-50/50 dark:bg-indigo-900/10",
                )}
                onClick={() => !notification.isRead && markAsRead(notification.id)}
              >
                <UserAvatar src={actorAvatar} name={actorName} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{actorName}</span> {notificationMessages[notification.type](notification)}
                  </p>
                  {notification.content && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">&quot;{notification.content}&quot;</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                  </p>
                </div>
                <Icon className={cn("h-5 w-5 shrink-0", notification.type === "upvote" ? "text-red-500" : "text-indigo-600")} />
              </div>
            )
          })}
        </div>
      ) : (
        <div className="py-12 text-center">
          <Bell className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No notifications</h3>
          <p className="mt-2 text-gray-500 dark:text-gray-400">You&apos;re all caught up!</p>
        </div>
      )}
    </div>
  )
}
